// 🎯 Single promo engine shared by POS cart, promo posters and any future caller.
// Pure functions — no React, no axios — so they are unit-tested (see promoEngine.test.js).

export const ruleTimeOk = (rule, now = new Date()) => {
  if (rule.start_date && now < new Date(rule.start_date + "T00:00:00")) return false;
  if (rule.end_date && now > new Date(rule.end_date + "T23:59:59")) return false;
  if (Array.isArray(rule.days) && rule.days.length > 0 && !rule.days.includes(now.getDay())) return false;
  if (rule.start_time && rule.end_time) {
    const hm = now.getHours() * 60 + now.getMinutes();
    const [sh, sm] = rule.start_time.split(":").map(Number);
    const [eh, em] = rule.end_time.split(":").map(Number);
    const s = sh * 60 + (sm || 0), e = eh * 60 + (em || 0);
    if (s <= e ? (hm < s || hm > e) : (hm < s && hm > e)) return false;
  }
  return true;
};

export const ruleScopeOk = (rule, p) =>
  !rule.apply_to || rule.apply_to === "all" || (rule.product_ids || []).includes(p.id) || (rule.category_ids || []).includes(p.category_id || p.category);

/** Discount ($) for ONE unit of a product at unit price `unitUsd` under `rule`. 0 if not applicable. */
export const unitPromoDiscount = (rule, unitUsd) => {
  if (rule.type === "discount") return unitUsd * ((parseFloat(rule.discount_value) || 0) / 100);
  if (rule.type === "flash_sale") { const fp = parseFloat(rule.flash_price) || 0; return fp > 0 && unitUsd > fp ? unitUsd - fp : 0; }
  return 0;
};

/** Item-level rule types (compete per cart line — only the best one applies) vs order-level (only the best one applies). */
export const ITEM_TYPES = ["second_half", "second_unit", "nth_unit", "discount", "buy_get", "flash_sale", "multi_price", "combo"];
export const ORDER_TYPES = ["full_reduction", "tiered_reduction"];

/** Highest tier reached for a tiered_reduction rule (tiers: [{min_amount, discount_value}]). */
export const tierReached = (rule, total) => (rule.tiers || [])
  .map(t => ({ min: parseFloat(t.min_amount) || 0, off: parseFloat(t.discount_value) || 0 }))
  .filter(t => t.min > 0 && t.off > 0 && total >= t.min)
  .sort((a, b) => b.min - a.min)[0] || null;

/**
 * Cart-level auto promo. `items` = [{ units, unitPrice, product }] where unitPrice is the SAME $ basis the
 * cart displays (Bs ÷ tasa in Bs mode). Returns { amount, label, perItem, gifts }.
 * Stacking policy (boss 2026-11-04): a line gets ONLY the single most generous item-level rule; the order gets
 * ONLY the single most generous order-level rule. `skipTypes` lets the webshop skip rules already baked into
 * unit prices (discount / flash_sale).
 */
export function computeAutoPromo({ items, rules, now = new Date(), skipTypes = [] }) {
  const names = new Set();
  const perItem = items.map(() => 0);
  const bestItem = items.map(() => ({ d: 0, name: "" }));
  const total = items.reduce((s, i) => s + i.units * i.unitPrice, 0);
  const idxFor = (rule) => items.map((i, idx) => (ruleScopeOk(rule, i.product || {}) ? idx : -1)).filter(idx => idx >= 0);
  const active = rules.filter(rule => rule.status === "active" && ruleTimeOk(rule, now) && !skipTypes.includes(rule.type));
  // ── item-level: evaluate each rule, keep the best per line ──
  for (const rule of active) {
    if (!ITEM_TYPES.includes(rule.type)) continue;
    const line = items.map(() => 0);
    if (rule.type === "second_half" || rule.type === "second_unit") {
      const pct = parseFloat(rule.second_percent ?? rule.percent) || 50;
      for (const idx of idxFor(rule)) { const i = items[idx]; line[idx] += Math.floor(i.units / 2) * i.unitPrice * pct / 100; }
    } else if (rule.type === "nth_unit") {
      const n = Math.max(2, parseInt(rule.nth) || 2), pct = parseFloat(rule.nth_percent) || 100;
      for (const idx of idxFor(rule)) { const i = items[idx]; line[idx] += Math.floor(i.units / n) * i.unitPrice * pct / 100; }
    } else if (rule.type === "discount") {
      if ((parseFloat(rule.min_amount) || 0) > total) continue;
      for (const idx of idxFor(rule)) { const i = items[idx]; line[idx] += i.units * unitPromoDiscount(rule, i.unitPrice); }
    } else if (rule.type === "buy_get") {
      const buy = parseInt(rule.buy_qty) || 2, get = parseInt(rule.get_qty) || 1;
      for (const idx of idxFor(rule)) { const i = items[idx]; line[idx] += Math.floor(i.units / (buy + get)) * get * i.unitPrice; }
    } else if (rule.type === "flash_sale") {
      for (const idx of idxFor(rule)) { const i = items[idx]; line[idx] += i.units * unitPromoDiscount(rule, i.unitPrice); }
    } else if (rule.type === "multi_price") {
      // 3 x $5: every full group of bundle_qty units of the SAME product sells for bundle_price
      const n = Math.max(2, parseInt(rule.bundle_qty) || 2), price = parseFloat(rule.bundle_price) || 0;
      if (price <= 0) continue;
      for (const idx of idxFor(rule)) { const i = items[idx]; const groups = Math.floor(i.units / n); const d = groups * (n * i.unitPrice - price); if (d > 0) line[idx] += d; }
    } else if (rule.type === "combo") {
      // A + B (+ C) together for combo_price: complete sets = min over required products
      const req = (rule.combo_items || []).map(c => ({ pid: c.product_id, qty: Math.max(1, parseInt(c.qty) || 1) })).filter(c => c.pid);
      const price = parseFloat(rule.combo_price) || 0;
      if (!req.length || price <= 0) continue;
      const byPid = {};
      items.forEach((i, idx) => { const pid = (i.product || {}).id; if (pid) (byPid[pid] = byPid[pid] || []).push(idx); });
      const unitsOf = (pid) => (byPid[pid] || []).reduce((s, idx) => s + items[idx].units, 0);
      const sets = Math.min(...req.map(c => Math.floor(unitsOf(c.pid) / c.qty)));
      if (!(sets > 0) || !isFinite(sets)) continue;
      // regular price of one set = Σ qty × cheapest unit price of that product in the cart
      const setPrice = req.reduce((s, c) => s + c.qty * Math.min(...(byPid[c.pid] || []).map(idx => items[idx].unitPrice)), 0);
      const d = sets * (setPrice - price);
      if (d <= 0) continue;
      const comboIdx = req.flatMap(c => byPid[c.pid] || []);
      spreadProportional(d, items.map((i, idx) => comboIdx.includes(idx) ? i : { units: 0, unitPrice: 0 }), line);
    }
    line.forEach((v, idx) => { if (v > bestItem[idx].d) bestItem[idx] = { d: v, name: rule.name || "Promo" }; });
  }
  bestItem.forEach((b, idx) => { if (b.d > 0) { perItem[idx] += b.d; names.add(b.name); } });
  // ── order-level: only the best rule ──
  let bestOrder = { d: 0, name: "" };
  for (const rule of active) {
    if (!ORDER_TYPES.includes(rule.type)) continue;
    let d = 0;
    if (rule.type === "full_reduction") {
      const minA = parseFloat(rule.min_amount) || 0;
      if (minA > 0 && total >= minA) d = parseFloat(rule.discount_value) || 0;
    } else if (rule.type === "tiered_reduction") {
      const t = tierReached(rule, total); if (t) d = t.off;
    }
    if (d > bestOrder.d) bestOrder = { d, name: rule.name || "Promo" };
  }
  if (bestOrder.d > 0) { spreadProportional(bestOrder.d, items, perItem); names.add(bestOrder.name); }
  // ── gifts: spend ≥ min_amount → gift_product_id × gift_qty at $0 (POS adds the line; shop shows the banner) ──
  const gifts = active.filter(r => r.type === "gift" && r.gift_product_id && total >= (parseFloat(r.min_amount) || 0) && (parseFloat(r.min_amount) || 0) > 0)
    .map(r => ({ rule_id: r.id, name: r.name || "Regalo", product_id: r.gift_product_id, qty: Math.max(1, parseInt(r.gift_qty) || 1) }));
  const amount = perItem.reduce((s, v) => s + v, 0);
  // Full precision: rounding to cents here distorts cheap items (8% of $0.757 = 0.0606 → 0.06 = 7.9%) and
  // makes the Bs total differ from the printed poster. Callers round only what they display/store.
  return { amount, label: Array.from(names).join(" + "), perItem, gifts };
}

/** Short human badge for a rule: "3 x $5", "3ª al 50%", "Combo $8", "-$5 desde $50"… (lang: zh/es/en/pt). */
export function promoBadge(rule, lang = "es", sym = "$") {
  if (!rule) return "";
  const zh = lang === "zh", en = lang === "en";
  const v = (x) => `${sym}${(parseFloat(x) || 0).toFixed(2).replace(/\.00$/, "")}`;
  switch (rule.type) {
    case "discount": return `${parseFloat(rule.discount_value) || 0}% OFF`;
    case "flash_sale": return `${zh ? "特价" : en ? "Only" : "Solo"} ${v(rule.flash_price)}`;
    case "buy_get": return zh ? `买${rule.buy_qty || 2}送${rule.get_qty || 1}` : `${(parseInt(rule.buy_qty) || 2) + (parseInt(rule.get_qty) || 1)} x ${rule.buy_qty || 2}`;
    case "second_half": case "second_unit": { const p = parseFloat(rule.second_percent ?? rule.percent) || 50; return zh ? `第2件${p === 100 ? "免费" : `${100 - p}折`}` : en ? `2nd unit -${p}%` : `2da unidad -${p}%`; }
    case "nth_unit": { const n = parseInt(rule.nth) || 2, p = parseFloat(rule.nth_percent) || 100; const ord = `${n}${[11, 12, 13].includes(n % 100) ? "th" : ({ 1: "st", 2: "nd", 3: "rd" })[n % 10] || "th"}`; return zh ? `第${n}件${p === 100 ? "免费" : `-${p}%`}` : p === 100 ? (en ? `${ord} FREE` : `${n}ª GRATIS`) : (en ? `${ord} unit -${p}%` : `${n}ª unidad -${p}%`); }
    case "multi_price": return `${rule.bundle_qty || 2} x ${v(rule.bundle_price)}`;
    case "combo": return `Combo ${v(rule.combo_price)}`;
    case "full_reduction": return zh ? `满${v(rule.min_amount)}减${v(rule.discount_value)}` : `-${v(rule.discount_value)} ${en ? "from" : "desde"} ${v(rule.min_amount)}`;
    case "tiered_reduction": { const ts = (rule.tiers || []).filter(t => parseFloat(t.min_amount) > 0).sort((a, b) => parseFloat(a.min_amount) - parseFloat(b.min_amount)); return ts.map(t => zh ? `满${v(t.min_amount)}减${v(t.discount_value)}` : `${v(t.min_amount)}→-${v(t.discount_value)}`).join(" · "); }
    case "gift": return zh ? `满${v(rule.min_amount)}送赠品` : en ? `Gift from ${v(rule.min_amount)}` : `Regalo desde ${v(rule.min_amount)}`;
    default: return rule.name || "";
  }
}

/** Unit price for a promo line: 2 decimals when unit × qty reproduces the net line (±½¢), else 4 decimals (buy 2 get 1 → 0.6667). */
export function promoUnit(netLine, qty) {
  if (!(qty > 0)) return 0;
  const x = netLine / qty;
  const u2 = Math.round(x * 100) / 100;
  return Math.abs(u2 * qty - netLine) < 0.005 ? u2 : Math.round(x * 10000) / 10000;
}

/**
 * Bs to deduct for an order-level promo when printing. New orders (promo_in_items) carry the promo in
 * their lines → 0. Legacy orders: prefer the exact Bs the server recorded (gross − total_amount_bs)
 * because promo_discount is rounded to cents ($0.05 × 871,37 = 43,57 while the customer paid 46,04 less).
 */
export function legacyPromoDeductBs(order, grossBs, rate) {
  if (order?.promo_in_items) return 0;
  const promoBs = (parseFloat(order?.promo_discount) || 0) * (rate || 0);
  if (!(promoBs > 0)) return 0;
  const srvBs = parseFloat(order?.total_amount_bs) || 0;
  const pts = parseFloat(order?.points_discount) || 0;
  if (!pts && srvBs > 0 && srvBs < grossBs - 0.01 && Math.abs(grossBs - srvBs - promoBs) < Math.max(5, promoBs * 0.2)) return grossBs - srvBs;
  return promoBs;
}

/** Bs the customer saved, for the "USTED AHORRO" line: exact per-line figure for new orders, deducted figure for legacy. */
export function promoSavedBs(order, deductBs, rate) {
  const promoBs = (parseFloat(order?.promo_discount) || 0) * (rate || 0);
  if (!(promoBs > 0)) return 0;
  if (!order?.promo_in_items) return deductBs || promoBs;
  const fromLines = (order.items || []).reduce((s, it) => {
    const lp = parseFloat(it.list_price_bs) || 0, up = parseFloat(it.unit_price_bs) || 0;
    return s + (lp > up ? (lp - up) * (parseFloat(it.quantity) || 1) : 0);
  }, 0);
  return fromLines > 0 ? fromLines : promoBs;
}

/** Spread an order-level $ discount across lines in proportion to their value (never above the line value). */
export function spreadProportional(discount, items, into) {
  const total = items.reduce((s, i) => s + i.units * i.unitPrice, 0);
  if (!(discount > 0) || !(total > 0)) return into;
  const d = Math.min(discount, total);
  items.forEach((i, idx) => { into[idx] = (into[idx] || 0) + d * (i.units * i.unitPrice) / total; });
  return into;
}

/**
 * Bs the POS will actually charge for one unit after a promo. Bs-priced products (stored price_bs1):
 * Bs = stored Bs − ($ discount × tasa) — exactly bsFinalTotalPreIva in PosPage. $-priced: $ × tasa.
 */
export function promoBsForProduct({ priceBs1, baseUsd, promoUsd, rate }) {
  const b1 = parseFloat(priceBs1) || 0;
  if (!(rate > 1)) return 0;
  if (b1 > 0 && baseUsd > 0) return Math.round((b1 - (baseUsd - promoUsd) * rate) * 100) / 100;
  return Math.round(promoUsd * rate * 100) / 100;
}

/** Bs display: whole numbers stay whole, otherwise 2 decimals (matches the receipt). */
export const fmtBs = (v) => Number.isInteger(v) ? v.toLocaleString("es-VE") : v.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export function promoPriceForProduct(rule, baseUsd) {
  if (!rule || !(baseUsd > 0)) return null;
  const d = unitPromoDiscount(rule, baseUsd);
  return d > 0 ? baseUsd - d : null;
}
